import { find, isEmpty, keyBy, map, omit, reject } from 'lodash';
import ClinicSettingsDB from 'src/fb/clinicSettings-db';
import GenericDB from 'src/fb/generic-db';
import TagsDB from 'src/fb/tags-db';
import { auth as useAuthStore } from 'src/store/auth';
import { bindDataBaseStore, unbindDataBaseStore } from 'src/utils/FirestoreUtils';

export default {

  setClinicTags(tags) {
    this.clinicTags = tags;
  }, // setClinicTags()

  setDefaultTags(tags) {
    this.defaultTags = tags;
  }, // setDefaultTags()

  addClinicTag(tag) {
    this.clinicTags = { ...this.clinicTags, [tag.id]: tag };
  }, // addClinicTags()

  updateClinicTag({ tag, settings }) {
    this.clinicTags = { ...this.clinicTags, [tag]: { id: tag, ...settings } };
  }, // updateClinicTags()

  removeTag(tag) {
    if (isEmpty(this.clinicTags)) {
      this.defaultTags = reject(this.defaultTags, { id: tag });
    } else {
      this.clinicTags = reject(this.clinicTags, { id: tag });
    }
  }, // removeTag()

  async createNewTag({ label, color = null, textColor = null }) {
    const tagId = label.split('/').join('-');
    const tagValueLower = tagId.toLowerCase();

    if (find(this.clinicTags, (tag) => tag.id.toLowerCase() === tagValueLower)) {
      // Already exists...
      return;
    }

    const auth = useAuthStore();

    const tagsDb = new TagsDB(auth.connectedClinic.id);

    const payload = {};

    if (color && textColor) {
      payload.color = color;
      payload.textColor = textColor;
    }

    if (isEmpty(this.clinicTags)) {
      // User has no custom tags. Save all new tags to clinic
      const newTag = { id: tagId, ...payload };
      const tags = [newTag, ...map(this.defaultTags, (tag) => tag)];
      await this.saveTagsToClinic(auth.connectedClinic.id, tags);
    }

    await tagsDb.create(payload, tagId);
  }, // createNewTag()

  async updateTagSettings({ clinicId, tagId, settings }) {
    const tagsDb = new TagsDB(clinicId);

    if (isEmpty(this.clinicTags)) {
      // User has no custom tags. Save all new tags to clinic
      const tags = map(this.defaultTags, (tag) => {
        const hasUpdate = tagId === tag.id && settings;
        const data = hasUpdate ? { ...tag, ...settings } : tag;
        return { id: tag.id, ...data };
      });

      await this.saveTagsToClinic(clinicId, tags);
      return;
    }

    const tagIdLower = tagId.toLowerCase();
    if (!find(this.clinicTags, (tag) => tag.id.toLowerCase() === tagIdLower)) {
      // Not exists...
      return;
    }

    await tagsDb.set(settings, tagId);
  }, // updateTagSettings()

  async deleteTag({ clinicId, tagId }) {
    const tagsDb = new TagsDB(clinicId);
    await tagsDb.delete(tagId);

    this.removeTag(tagId);

    if (isEmpty(this.clinicTags)) {
      // User has no custom tags. Save all defaults to clinic
      await this.saveTagsToClinic(clinicId, this.defaultTags);
    }
  }, // deleteTag()

  async saveTagsToClinic(clinicId, tags) {
    const genericDb = new GenericDB();
    const tagsDb = new TagsDB(clinicId);
    const clinicSettingsDb = new ClinicSettingsDB();

    try {
      const tagsToSave = tags.map((tag) => ({
        // We should usually have the `id` property within the tag object, as it is the tag name. This is optional
        // to avoid getting an error if the `id` is not there for some reason
        ...(tag.id ? { id: tag.id } : {}),
        data: omit(tag, 'id'),
      }));

      // Update UI first and then save all tags to clinic
      const tagList = keyBy(tagsToSave, 'id');
      this.setClinicTags(tagList);
      this.setDefaultTags({});

      await genericDb.executeTransaction((transactionMethods) => {
        const createClinicTagPromises = map(tagsToSave, (tag) => {
          return transactionMethods.create(tagsDb, tag);
        });

        const setFlagPromise = transactionMethods.set(
          clinicSettingsDb,
          { id: clinicId, data: { isDefaultTagsInitialized: true }, merge: true },
        );

        return Promise.all([...createClinicTagPromises, setFlagPromise]);
      });

      this.setIsDefaultTagsInitialized(true);
    } catch (err) {
      console.error(`Transaction failed: ${err.message}`, {
        clinicId,
        defaultTags: this.defaultTags,
        error: err,
      });
      throw err;
    }
  }, // saveTagsToClinic()

  async reloadTags({ clinicId }) {
    if (!clinicId) {
      return this.reloadDefaultTags();
    }

    const tagsDb = new TagsDB(clinicId);
    const tags = await tagsDb.readAll();

    if (isEmpty(tags) && !this.isDefaultTagsInitialized) {
      // Read default tags
      return this.reloadDefaultTags();
    }

    const tagList = keyBy(tags, 'id');
    this.setClinicTags(tagList);
  }, // reloadTags()

  async reloadDefaultTags() {
    const tagsDb = new TagsDB('_default_');
    const defaultTags = await tagsDb.readAll();
    const defaultTagList = keyBy(defaultTags, 'id');

    this.setDefaultTags(defaultTagList);
  }, // reloadDefaultTags()

  setUnsavedTags(tags) {
    this.unsavedClinicTags = tags;
  }, // setUnsavedTags()

  async saveUnsavedTags() {
    const saveTagActions = map(this.unsavedClinicTags, (tag) => this.createNewTag({
      label: tag,
    }));

    await Promise.all(saveTagActions);

    this.setUnsavedTags([]);
  }, // saveUnsavedTags()

  setIsDefaultTagsInitialized(isDefaultTagsInitialized) {
    this.isDefaultTagsInitialized = isDefaultTagsInitialized;
  }, // setIsDefaultTagsInitialized()

  async bindClinicTags(clinicId) {
    if (!clinicId) {
      return this.reloadDefaultTags();
    }

    const tagsDb = new TagsDB(clinicId);
    const ref = tagsDb.getQuery();

    const clinicTags = await bindDataBaseStore({
      store: this,
      property: 'clinicTags',
      action: 'onClinicTagsChanged',
    }, ref, {});

    if (isEmpty(clinicTags) && !this.isDefaultTagsInitialized) {
      // Read default tags
      return this.reloadDefaultTags();
    }
  }, // bindClinicTags()

  onClinicTagsChanged(clinicTags) {
    const tagList = keyBy(clinicTags, 'id');
    this.setClinicTags(tagList);

    if (!isEmpty(clinicTags) && !this.isDefaultTagsInitialized) {
      // Defaults tags was saved to clinic by other user and then they all was deleted
      this.setDefaultTags({});
      this.setIsDefaultTagsInitialized(true);
    }
  }, // onClinicTagsChanged()

  unbindClinicTags() {
    return unbindDataBaseStore({ store: this, property: 'clinicTags' }, {});
  }, // unbindClinicTags()

  logout() {
    this.unbindClinicTags();
    this.setClinicTags({});
    this.setUnsavedTags([]);
    this.setDefaultTags({});
    this.setIsDefaultTagsInitialized(false);
  }, // logout()

};
